import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Container from "../components/Container";
import Button, { buttonClasses } from "../components/form/button";

import { DiJavascript1 } from "react-icons/di";
import { FaAngular, FaCss3, FaDocker, FaHtml5, FaJava, FaPython, FaReact, FaVuejs } from "react-icons/fa";
import { SiTailwindcss, SiTypescript } from "react-icons/si";
import { TbBrandCpp } from "react-icons/tb";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import "react-vertical-timeline-component/style.min.css";
import { motion } from "framer-motion";

export default function Home() {
    return (
        <>
            <Helmet>
                <title>ReinVB — Home</title>
                <meta name="description" content="Rein Vanbelleghem, A Industrial Engineer Informatics with a heart for coding!" />
            </Helmet>

            <Container className="mt-4 lg:mt-8 xl:mt-16">
                <div className="mt-16 flex items-center justify-between gap-8">
                    <div>
                        <h1 className="max-w-sm text-5xl font-bold">
                            <motion.div
                                animate={{ rotate: [0, 20, 0, 20, 0, 20, 0, 20, 0] }}
                                transition={{
                                    duration: 1.6,
                                    ease: "easeInOut",
                                    repeat: Infinity,
                                    repeatDelay: 5,
                                    type: "tween"
                                }}
                                className="inline-block w-fit pr-4">
                                👋
                            </motion.div>
                            Hi, I'm <span className="text-blue-400 dark:text-blue-500">Rein</span> Vanbelleghem.
                        </h1>
                        <h2 className="text-2xl font-medium text-slate-500 dark:text-slate-500">An Industrial Engineer Informatics</h2>
                        <div className="mt-8 flex gap-2">
                            <Link to="/contact">
                                <Button variant={"dark"}>Contact</Button>
                            </Link>
                            <Link to="/portfolio">
                                <Button variant={"suttle"}>See my work?</Button>
                            </Link>
                        </div>
                    </div>
                    <div className="hidden md:block">
                        <img src="/images/me_2.jpeg" className="w-full max-w-xs rounded-2xl" alt="Rein Vanbelleghem" />
                    </div>
                </div>
            </Container>
            <div className="mt-32 bg-gradient-to-t from-blue-600 to-blue-500 py-16 text-blue-100 dark:bg-blue-800">
                <Container variant={"small"}>
                    <h2 className="text-center text-3xl font-medium text-white">About me.</h2>
                    <p className="mt-4">
                        I'm Rein Vanbelleghem, a student of Industrial Engineering Informatics at the University of Ghent. With a passion for cutting-edge programming languages, I create visually stunning websites. My eye for design detail and dedication to hard work set me apart. Join me as I
                        navigate the digital landscape, bringing ideas to life.
                    </p>
                    <Link to="/about" className={buttonClasses({ variant: "ghost", size: "ghost", className: "group mt-8 text-sm italic hover:opacity-80" })}>
                        Read More
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4 transition-all group-hover:ml-1">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75" />
                        </svg>
                    </Link>
                </Container>
            </div>

            <Container className="mt-32">
                <h2 className="text-4xl font-medium">My qualities.</h2>

                <div className="mt-12 grid grid-cols-4 justify-items-stretch gap-12 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12">
                    <FaReact size={"100%"} className="text-blue-400 transition-all hover:scale-110" />

                    <FaHtml5 size={"100%"} className="text-orange-600 transition-all hover:scale-110 dark:text-orange-400" />

                    <DiJavascript1 size={"100%"} className="text-yellow-300 transition-all hover:scale-110" />

                    <SiTypescript size={"100%"} className="text-blue-500 transition-all hover:scale-110" />

                    <FaCss3 size={"100%"} className="text-emerald-400 transition-all hover:scale-110" />

                    <SiTailwindcss size={"100%"} className="text-blue-400 transition-all hover:scale-110" />

                    <FaAngular size={"100%"} className="text-red-400 transition-all hover:scale-110" />

                    <FaVuejs size={"100%"} className="text-green-400 transition-all hover:scale-110" />

                    <FaPython size={"100%"} className="text-blue-800 transition-all hover:scale-110 dark:text-blue-600" />

                    <TbBrandCpp size={"100%"} className="text-blue-500 transition-all hover:scale-110" />

                    <FaJava size={"100%"} className="text-red-400 transition-all hover:scale-110" />

                    <FaDocker size={"100%"} className="text-blue-400 transition-all hover:scale-110" />
                </div>
                <div className="mb-16 mt-4 text-center italic text-slate-300 dark:text-slate-700">and many more...</div>
            </Container>

            <Container className="my-16">
                <h2 className="mb-8 text-4xl font-medium">My experience.</h2>

                <VerticalTimeline lineColor="rgba(100, 116, 139, 0.1)">
                    <VerticalTimelineElement
                        textClassName="border !shadow dark:bg-slate-800"
                        contentArrowStyle={{ borderRight: "7px solid rgba(100, 116, 139, 0.1)" }}
                        date="2023 - present"
                        iconClassName="bg-blue-500 border-4 border-blue-600 text-blue-200 !shadow-none"
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-8 w-8">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
                                />
                            </svg>
                        }>
                        <h3 className="font-semibold">Master Industrial Engineer Informatics</h3>
                        <h4 className="dark:text-slate-400† text-slate-500">Ghent University</h4>
                        <p className="!text-sm !font-normal">Finishing up my degree in becoming an Industrial Engineer.</p>
                    </VerticalTimelineElement>
                    <VerticalTimelineElement
                        textClassName="border !shadow dark:bg-slate-800"
                        contentArrowStyle={{ borderRight: "7px solid rgba(100, 116, 139, 0.1)" }}
                        date="Summer of 2023"
                        iconClassName="bg-emerald-500 border-4 border-emerald-600 text-emerald-200 !shadow-none"
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-8 w-8">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
                                />
                            </svg>
                        }>
                        <h3 className="font-semibold">Internship at Skyline Communications</h3>
                        <h4 className="dark:text-slate-400† text-slate-500">Skyline Park, Izegem</h4>
                        <p className="!text-sm !font-normal">Developing a Chrome extension for debugging Dataminer webapplications.</p>
                    </VerticalTimelineElement>
                    <VerticalTimelineElement
                        textClassName="border !shadow dark:bg-slate-800"
                        contentArrowStyle={{ borderRight: "7px solid rgba(100, 116, 139, 0.1)" }}
                        date="Summer of 2022"
                        iconClassName="bg-orange-500 border-4 border-orange-600 text-orange-200 !shadow-none"
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-8 w-8">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z"
                                />
                            </svg>
                        }>
                        <h3 className="font-semibold">Summerjob at Peliqan</h3>
                        <h4 className="dark:text-slate-400† text-slate-500">Peliqan.io</h4>
                        <p className="!text-sm !font-normal">Frontend vue development at Peliqan</p>
                    </VerticalTimelineElement>
                    <VerticalTimelineElement
                        textClassName="border !shadow dark:bg-slate-800"
                        contentArrowStyle={{ borderRight: "7px solid rgba(100, 116, 139, 0.1)" }}
                        date="2020 - 2023"
                        iconClassName="bg-indigo-500 border-4 border-indigo-600 text-indigo-200 !shadow-none"
                        icon={
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-8 w-8">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
                                />
                            </svg>
                        }>
                        <h3 className="font-semibold">Bachelor Industrial Engineer Informatics</h3>
                        <h4 className="dark:text-slate-400† text-slate-500">Ghent University</h4>
                        <p className="!text-sm !font-normal">Finished my degree.</p>
                    </VerticalTimelineElement>
                </VerticalTimeline>
            </Container>
        </>
    );
}
